import html2canvas from "html2canvas";

export const htmlToImage = async (
	elementId: string,
	name: string = "1.jpeg",
	maxSize = 100 * 1024,
): Promise<File> => {
	try {
		const element = document.getElementById(elementId);
		if (!element) return new File([], name, { type: "image/jpeg" });

		const canvas = await captureElementAsCanvas(element);
		return await convertCanvasToImage(canvas, name, maxSize);
	} catch (error) {
		console.error("Error capturing HTML as image:", error);
		throw new Error("Error capturing HTML as image");
	}
};

// Extracted function to capture the element as a canvas
const captureElementAsCanvas = async (
	element: HTMLElement,
): Promise<HTMLCanvasElement> => {
	return await html2canvas(element, {
		onclone: (newDom) => {
			const clonedElement = newDom.getElementById(element.id);
			if (clonedElement) clonedElement.style.display = "block";
		},
	});
};

// Extracted function to convert canvas to image file
const convertCanvasToImage = (
	canvas: HTMLCanvasElement,
	name: string,
	maxSize: number,
): Promise<File> => {
	return new Promise((resolve, reject) => {
		canvas.toBlob((blob) => {
			if (!blob) {
				reject(new Error("Blob is null"));
				return;
			}

			let image = new File([blob], name, { type: "image/jpeg" });
			// Handle the async operation properly
			compressIfNeeded(image, maxSize)
				.then(resolve)
				.catch((error) =>
					reject(error instanceof Error ? error : new Error(`${error}`)),
				);
		}, "image/jpeg");
	});
};

// ==========================
// FALLBACK IMAGE LOADER
// ==========================
function loadImage(file: Blob): Promise<HTMLImageElement> {
	return new Promise((resolve, reject) => {
		const img = new Image();
		const url = URL.createObjectURL(file);

		img.onload = () => {
			URL.revokeObjectURL(url);
			resolve(img);
		};

		img.onerror = () => reject(new Error("Failed to load image"));
		img.src = url;
	});
}

// ==========================
// IMAGE SOURCE LOADER (MODERN + FALLBACK)
// ==========================
async function loadImageSource(file: File): Promise<{
	source: CanvasImageSource;
	width: number;
	height: number;
	cleanup: () => void;
}> {
	// ✅ Try modern approach (handles EXIF orientation)
	// Prefer `globalThis` over `window`
	
	if ("createImageBitmap" in globalThis) {
		try {
			const bitmap = await createImageBitmap(file, {
				imageOrientation: "from-image" as ImageOrientation,
			});

			return {
				source: bitmap,
				width: bitmap.width,
				height: bitmap.height,
				cleanup: () => bitmap.close(),
			};
		} catch (e) {
			console.warn("createImageBitmap failed, using fallback", e);
		}
	} else {
		console.warn("createImageBitmap not supported, using fallback");
	}

	// ✅ Fallback (always works)
	const img = await loadImage(file);

	return {
		source: img,
		width: img.naturalWidth || img.width,
		height: img.naturalHeight || img.height,
		cleanup: () => {
			img.src = "";
		},
	};
}

// ==========================
// QUALITY HELPER
// ==========================
const getQuality = (mp: number): number => {
	if (mp > 1) return 0.77;
	if (mp > 0.5) return 0.82;
	return 0.87;
};

// ==========================
// CANVAS SETUP
// ==========================
function createCanvas(): {
	canvas: HTMLCanvasElement;
	ctx: CanvasRenderingContext2D;
} {
	const canvas = document.createElement("canvas");
	const ctx = canvas.getContext("2d");

	if (!ctx) throw new Error("Canvas context not available");

	ctx.imageSmoothingEnabled = true;
	ctx.imageSmoothingQuality = "high";

	return { canvas, ctx };
}

// ==========================
// SCALE CALCULATION
// ==========================
function calcInitialScale(
	width: number,
	height: number,
	maxDimension: number
): number {
	return Math.min(1, maxDimension / width, maxDimension / height);
}

// ==========================
// CLEANUP
// ==========================
function releaseResources(canvas: HTMLCanvasElement): void {
	canvas.width = 0;
	canvas.height = 0;
}

// ==========================
// CANVAS → BLOB
// ==========================
function toBlob(
	canvas: HTMLCanvasElement,
	quality: number
): Promise<Blob> {
	return new Promise((resolve, reject) =>
		canvas.toBlob(
			(b) => (b ? resolve(b) : reject(new Error("Compression failed"))),
			"image/jpeg",
			quality
		)
	);
}

// ==========================
// QUALITY REDUCTION
// ==========================
async function stepDownQuality(
	canvas: HTMLCanvasElement,
	width: number,
	height: number,
	config: any
): Promise<{ blob: Blob | null; found: boolean }> {
	const mp = (width * height) / 1_000_000;
	let quality = getQuality(mp);

	let smallestBlob: Blob | null = null;

	while (quality >= config.QUALITY_FLOOR) {
		const blob = await toBlob(canvas, quality);

		if (blob.size <= config.TARGET_SIZE) {
			return { blob, found: true };
		}

		if (!smallestBlob || blob.size < smallestBlob.size) {
			smallestBlob = blob;
		}

		quality = Math.round((quality - config.QUALITY_STEP) * 100) / 100;
	}

	return { blob: smallestBlob, found: false };
}

// ==========================
// MAIN COMPRESSION LOOP
// ==========================
async function attemptCompression(
	source: CanvasImageSource,
	originalWidth: number,
	originalHeight: number,
	canvas: HTMLCanvasElement,
	ctx: CanvasRenderingContext2D,
	initialScale: number,
	config: any
): Promise<Blob | null> {
	let bestBlob: Blob | null = null;
	let dimScale = initialScale;

	for (let attempt = 0; attempt < config.MAX_DIMENSION_ATTEMPTS; attempt++) {
		const width = Math.round(originalWidth * dimScale);
		const height = Math.round(originalHeight * dimScale);

		if (width < 200 || height < 200) break;

		canvas.width = width;
		canvas.height = height;

		ctx.drawImage(source, 0, 0, width, height);

		const { blob, found } = await stepDownQuality(
			canvas,
			width,
			height,
			config
		);

		if (blob && (!bestBlob || blob.size < bestBlob.size)) {
			bestBlob = blob;
		}

		if (found) break;

		dimScale *= config.DIMENSION_STEP;
	}

	return bestBlob;
}

// ==========================
// MAIN FUNCTION
// ==========================
async function compressImageToTargetSize(
	file: File,
	maxSizeBytes: number
): Promise<Blob> {
	const CONFIG = {
		TARGET_SIZE: maxSizeBytes,
		MAX_DIMENSION: 1600,
		QUALITY_FLOOR: 0.52,
		QUALITY_STEP: 0.05,
		DIMENSION_STEP: 0.85,
		MAX_DIMENSION_ATTEMPTS: 6,
	} as const;

	if (file.size <= CONFIG.TARGET_SIZE) return file;

	const {
		source,
		width: originalWidth,
		height: originalHeight,
		cleanup,
	} = await loadImageSource(file);

	const { canvas, ctx } = createCanvas();

	const initialScale = calcInitialScale(
		originalWidth,
		originalHeight,
		CONFIG.MAX_DIMENSION
	);

	const bestBlob = await attemptCompression(
		source,
		originalWidth,
		originalHeight,
		canvas,
		ctx,
		initialScale,
		CONFIG
	);

	// ✅ cleanup everything
	cleanup();
	releaseResources(canvas);

	if (!bestBlob) {
		throw new Error("Could not compress image to target size");
	}

	return bestBlob;
}

// ==========================
// SINGLE IMAGE
// ==========================
async function compressSingleImage(
	file: File,
	maxSizeBytes: number,
): Promise<File> {
	if (!file.type.startsWith("image/")) return file;

	try {
		if (file.size <= maxSizeBytes) return file;

		const compressedBlob = await compressImageToTargetSize(
			file,
			maxSizeBytes,
		);

		return new File([compressedBlob], file.name, {
			type: file.type,
			lastModified: Date.now(),
		});
	} catch (error) {
		console.error("Compression error:", error);
		return file;
	}
}

// ==========================
// MAIN FUNCTION (SUPPORT ARRAY)
// ==========================
export async function compressImage(
	fileOrFiles: File | File[],
	maxSizeBytes = 200 * 1024, // 200KB default
): Promise<File | File[]> {
	const isArray = Array.isArray(fileOrFiles);
	const files = isArray ? fileOrFiles : [fileOrFiles];

	const result = await Promise.all(
		files.map((file) =>
			compressSingleImage(file, maxSizeBytes),
		),
	);

	return isArray ? result : result[0];
}

// ==========================
// HELPER (OPTIONAL)
// ==========================
export const compressIfNeeded = async (
	image: File,
	maxSizeBytes: number,
): Promise<File> => {
	if (image.size <= maxSizeBytes) return image;

	return (await compressImage(image, maxSizeBytes)) as File;
};
